'use client';

import { DirectionProvider } from '@base-ui/react/direction-provider';
import { Slider as SliderPrimitive } from '@base-ui/react/slider';
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';

import { cn } from '@/lib/utils';
import { getSliderBounds, normalizeSliderValues } from './slider-utils';

export type SliderDirection = 'ltr' | 'rtl';
export type SliderSize = 'sm' | 'md' | 'lg';
export type SliderColor = 'primary' | 'info' | 'success' | 'warning' | 'error';

export interface SliderProps extends SliderPrimitive.Root.Props {
  /** Direction for RTL support - auto-detected from DOM if not provided */
  dir?: SliderDirection;
  /** Size of the slider track */
  size?: SliderSize;
  /** Color variant of the slider */
  color?: SliderColor;
}

/**
 * Hook to detect direction from nearest ancestor with dir attribute
 */
export function useAutoDirection(explicitDir?: SliderDirection) {
  const ref = useRef<HTMLDivElement>(null);
  const [detectedDir, setDetectedDir] = useState<SliderDirection>('ltr');

  useEffect(() => {
    const detect = () => {
      if (ref.current) {
        const computed = getComputedStyle(ref.current).direction;
        setDetectedDir(computed === 'rtl' ? 'rtl' : 'ltr');
      }
    };

    detect();

    // Observe only the closest [dir] ancestor and documentElement (at most 2 targets)
    const observer = new MutationObserver(detect);
    if (ref.current) {
      const closest = ref.current.closest('[dir]');
      const docEl = ref.current.ownerDocument.documentElement;
      if (closest) {
        observer.observe(closest, {
          attributes: true,
          attributeFilter: ['dir'],
        });
      }
      if (docEl !== closest) {
        observer.observe(docEl, { attributes: true, attributeFilter: ['dir'] });
      }
    }
    return () => observer.disconnect();
  }, []);

  return { ref, direction: explicitDir ?? detectedDir };
}

export const trackSizeClasses: Record<SliderSize, string> = {
  sm: 'data-[orientation=horizontal]:h-1.5 data-[orientation=vertical]:w-1.5',
  md: 'data-[orientation=horizontal]:h-2 data-[orientation=vertical]:w-2',
  lg: 'data-[orientation=horizontal]:h-3 data-[orientation=vertical]:w-3',
};

export const colorClasses: Record<
  SliderColor,
  { indicator: string; track: string; thumb: string }
> = {
  primary: {
    indicator: 'bg-primary',
    track: 'bg-muted',
    thumb: 'border-primary ring-primary/20',
  },
  info: {
    indicator: 'bg-info',
    track: 'bg-info/20',
    thumb: 'border-info ring-info/20',
  },
  success: {
    indicator: 'bg-success',
    track: 'bg-success/20',
    thumb: 'border-success ring-success/20',
  },
  warning: {
    indicator: 'bg-warning',
    track: 'bg-warning/20',
    thumb: 'border-warning ring-warning/20',
  },
  error: {
    indicator: 'bg-destructive',
    track: 'bg-destructive/20',
    thumb: 'border-destructive ring-destructive/20',
  },
};

function getThumbSlotIds(count: number): string[] {
  return Array.from({ length: count }, (_, thumbSlot) => `thumb-${thumbSlot}`);
}

const Slider = forwardRef<HTMLDivElement, SliderProps>(
  (
    {
      className,
      defaultValue,
      value,
      min = 0,
      max = 100,
      dir,
      size = 'md',
      color = 'primary',
      ...props
    },
    forwardedRef,
  ) => {
    const { ref, direction } = useAutoDirection(dir);
    const colors = colorClasses[color];
    const bounds = useMemo(() => getSliderBounds(min, max), [min, max]);
    const safeMin = bounds.min;
    const safeMax = bounds.max;
    const safeValue = useMemo(
      () => normalizeSliderValues(value, safeMin, safeMax),
      [value, safeMin, safeMax],
    );
    const safeDefaultValue = useMemo(
      () => normalizeSliderValues(defaultValue, safeMin, safeMax),
      [defaultValue, safeMin, safeMax],
    );

    const _values = useMemo(
      () => safeValue ?? safeDefaultValue ?? [safeMin],
      [safeValue, safeDefaultValue, safeMin],
    );

    return (
      <div ref={ref} className="contents">
        <DirectionProvider direction={direction}>
          <SliderPrimitive.Root
            ref={forwardedRef}
            className="data-[orientation=horizontal]:w-full data-[orientation=vertical]:h-full"
            data-slot="slider"
            data-color={color}
            data-size={size}
            defaultValue={safeDefaultValue}
            value={safeValue}
            min={safeMin}
            max={safeMax}
            {...props}
          >
            <SliderPrimitive.Control
              className={cn(
                'group/slider data-[orientation=vertical]:min-h-40 relative flex w-full touch-none items-center select-none data-disabled:opacity-50 data-[orientation=vertical]:h-full data-[orientation=vertical]:w-auto data-[orientation=vertical]:flex-col',
                className,
              )}
            >
              <SliderPrimitive.Track
                data-slot="slider-track"
                className={cn(
                  'rounded-full data-[orientation=horizontal]:w-full data-[orientation=vertical]:h-full relative overflow-hidden select-none',
                  trackSizeClasses[size],
                  colors.track,
                )}
              >
                <SliderPrimitive.Indicator
                  data-slot="slider-range"
                  className={cn(
                    'select-none data-[orientation=horizontal]:h-full data-[orientation=vertical]:w-full',
                    colors.indicator,
                  )}
                />
              </SliderPrimitive.Track>
              {getThumbSlotIds(_values.length).map((thumbId) => (
                <SliderPrimitive.Thumb
                  data-slot="slider-thumb"
                  key={thumbId}
                  className={cn(
                    'relative size-5 rounded-full border-2 bg-background shadow-sm transition-[color,box-shadow] duration-150 ease-out hover:ring-4 focus-visible:ring-4 data-[dragging]:shadow-md focus-visible:outline-hidden block shrink-0 select-none disabled:pointer-events-none disabled:opacity-50 motion-reduce:transition-none before:absolute before:-inset-3 before:rounded-full',
                    colors.thumb,
                  )}
                />
              ))}
            </SliderPrimitive.Control>
          </SliderPrimitive.Root>
        </DirectionProvider>
      </div>
    );
  },
);

Slider.displayName = 'Slider';

export { Slider };
